import { I18n } from '@affine/i18n';

import type {
  DatabaseAttachment,
  DatabaseAttachmentPriority,
  DatabaseAttachmentScope,
  DatabaseAttachmentSource,
} from '../ai-chat-content/type';

export function getDatabaseAttachmentLabel(
  database: DatabaseAttachment
): string {
  if (database.selectedRowIds?.length) {
    return `${database.name}: ${database.selectedRowIds.length} selected rows`;
  }

  const visibleRowWindow = database.visibleRowWindow;
  if (visibleRowWindow && visibleRowWindow.rowIds.length > 0) {
    const start = visibleRowWindow.offset + 1;
    const end = visibleRowWindow.offset + visibleRowWindow.rowIds.length;
    return I18n.t('com.streamlain.ai.context.database.rows', {
      name: database.name,
      start,
      end,
    });
  }

  if (visibleRowWindow) {
    return I18n.t('com.streamlain.ai.context.database.rows-empty', {
      name: database.name,
    });
  }

  return database.name;
}

export function getDatabaseAttachmentMeta(
  database: DatabaseAttachment
): string {
  const rowCount =
    database.scope === 'selected-rows'
      ? database.selectedRowIds?.length
      : database.visibleRowWindow?.rowIds.length;
  const projectedColumnCount =
    database.projectedColumnIds?.length ?? database.columns.length;
  const totalColumnCount = database.totalColumnCount ?? database.columns.length;
  const rows =
    rowCount === undefined
      ? getDatabaseScopeLabel(database.scope)
      : `${rowCount} ${database.scope === 'selected-rows' ? 'selected rows' : 'visible rows'}`;
  return `${rows} · ${projectedColumnCount} of ${totalColumnCount} columns`;
}

export function getDatabaseAttachmentTooltip(
  database: DatabaseAttachment
): string {
  const columnList = database.columns.length
    ? database.columns.map(col => `  - ${col.name} (${col.type})`).join('\n')
    : `  - ${I18n.t('com.streamlain.ai.context.database.tooltip.noColumns')}`;

  return [
    I18n.t('com.streamlain.ai.context.database.tooltip.database', {
      name: database.name,
    }),
    `${I18n.t(
      'com.streamlain.ai.context.database.tooltip.scope'
    )}: ${getDatabaseScopeDetail(database)}`,
    `${I18n.t(
      'com.streamlain.ai.context.database.tooltip.source'
    )}: ${getDatabaseSourceLabel(database.source)}`,
    `${I18n.t(
      'com.streamlain.ai.context.database.tooltip.priority'
    )}: ${getDatabasePriorityLabel(database.priority)}`,
    `${I18n.t('com.streamlain.ai.context.database.tooltip.columns')}:\n${columnList}`,
  ].join('\n');
}

function getDatabaseScopeDetail(database: DatabaseAttachment): string {
  if (database.selectedRowLabel) {
    return `${getDatabaseScopeLabel('selected-rows')} (${database.selectedRowLabel})`;
  }

  const visibleRowWindow = database.visibleRowWindow;
  if (visibleRowWindow && visibleRowWindow.rowIds.length > 0) {
    return I18n.t('com.streamlain.ai.context.database.tooltip.visibleRows', {
      start: visibleRowWindow.offset + 1,
      end: visibleRowWindow.offset + visibleRowWindow.rowIds.length,
      total: visibleRowWindow.totalCount,
    });
  }

  if (visibleRowWindow) {
    return I18n.t(
      'com.streamlain.ai.context.database.tooltip.visibleRowsEmpty',
      {
        total: visibleRowWindow.totalCount,
      }
    );
  }

  return getDatabaseScopeLabel(database.scope);
}

function getDatabaseScopeLabel(
  scope: DatabaseAttachmentScope | undefined
): string {
  switch (scope ?? 'metadata-only') {
    case 'selected-rows':
      return I18n.t('com.streamlain.ai.context.database.scope.selectedRows');
    case 'visible-window':
      return I18n.t('com.streamlain.ai.context.database.scope.visibleWindow');
    case 'metadata-only':
      return I18n.t('com.streamlain.ai.context.database.scope.metadataOnly');
  }
}

function getDatabaseSourceLabel(source: DatabaseAttachmentSource): string {
  switch (source) {
    case 'focus':
      return I18n.t('com.streamlain.ai.context.database.source.focus');
    case 'fullscreen':
      return I18n.t('com.streamlain.ai.context.database.source.fullscreen');
    case 'paste':
      return I18n.t('com.streamlain.ai.context.database.source.paste');
  }
}

function getDatabasePriorityLabel(
  priority: DatabaseAttachmentPriority
): string {
  switch (priority) {
    case 'highest':
      return I18n.t('com.streamlain.ai.context.database.priority.highest');
    case 'mid':
      return I18n.t('com.streamlain.ai.context.database.priority.mid');
    case 'lowest':
      return I18n.t('com.streamlain.ai.context.database.priority.lowest');
  }
}
